// SideMenuAdmin.tsx
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { versionPageLabels } from '@/constants/homologations';
import { permissionsKeys as TemplatesPermissionsKeys } from '@/modules/Template/permissionsKeys';
import { cx, isCurrentPathOrChild } from '@/utils';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  Card,
  List,
  ListItem,
  ListItemPrefix,
  Typography,
} from '@material-tailwind/react';
import { produce } from 'immer';

import {
  ROUTE_ADMIN_CONFIG_PAGE_INSTITUTIONAL_PROGRAMS,
  ROUTE_ADMIN_CONFIG_PAGE_ROOM,
  ROUTE_ADMIN_FOOTER_OBJECTS,
  ROUTE_ADMIN_HEADER_OBJECTS,
  ROUTE_ADMIN_HOME_PAGE,
  ROUTE_ADMIN_MICRO_SITES,
  ROUTE_ADMIN_MICRO_SITES_CUSTOM_PAGE,
  ROUTE_ADMIN_MICRO_SITES_DEFAULT_PAGE,
  ROUTE_ADMIN_MICRO_SITES_DYNAMIC_PAGE,
  ROUTE_ADMIN_MICRO_SITES_PAGE,
  ROUTE_ADMIN_PAGE_CONFIG,
  ROUTE_ADMIN_PARAMETRIZATION,
  ROUTE_ADMIN_PARAMETRIZATION_DEPENDENCIES,
  ROUTE_ADMIN_PARAMETRIZATION_MICRO_SITES,
  ROUTE_ADMIN_PARAMETRIZATION_SECURITY,
  ROUTE_ADMIN_PARAMETRIZATION_SYSTEM_PARAMETERS,
  ROUTE_ADMIN_PARAMETRIZATION_THEMES,
  ROUTE_ADMIN_PARTICIPATES_PAGE,
  ROUTE_ADMIN_PQRSD_PAGE,
  ROUTE_ADMIN_REGULATIONS_PAGE,
  ROUTE_ADMIN_SERVICES_PAGE,
  ROUTE_ADMIN_TEMPLATES,
  ROUTE_ADMIN_TRANSPARENCY_PAGE,
  ROUTE_ADMIN_VERSION_PAGE_PAGE_VERSION,
  ROUTE_DOCUMENTS_MANAGEMENT,
  ROUTE_DYNAMIC_PAGES_MANAGEMENT,
  ROUTE_EVENT_MANAGEMENT,
  ROUTE_INSTITUTIONAL_PROGRAMS_MANAGEMENT,
  ROUTE_PARTICIPATE_CONTENT_MANAGEMENT,
  ROUTE_PQRSD_CONTENT_MANAGEMENT,
  ROUTE_PRESS_ROOM_MANAGEMENT,
  ROUTE_PROCEDURES_SERVICES_MANAGEMENT,
  ROUTE_REGULATION_MANAGEMENT,
} from '@/utils/constants';
import useUserStore, { useSelectedPageStore } from '@/utils/store/store';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/common/HoverCard';
import SvgConfiguration from '@/components/common/Icons/Configuration';
import IconConfigPage from '@/components/common/Icons/IconConfigPage';
import IconPages from '@/components/common/Icons/IconPages';
import PanelLeftClose from '@/components/common/Icons/PanelLeftClose';
import PanelLeftOpen from '@/components/common/Icons/PanelLeftOpen';
import SideArrowUp from '@/components/common/Icons/SideArrowUp';
import SideContent from '@/components/common/Icons/SideContent';
import SidePen from '@/components/common/Icons/SidePen';
import SideReport from '@/components/common/Icons/SideReport';
import {
  DependencyType,
  UserTypes,
  VersionStates,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { permissionsKeys as EventsPermissionsKeys } from '@/components/views/Admin/private/ContentManagement/EventsAdm/PermissionsKey';
import { permissionsKeys as InstitutionalProgramsContentManagementPermissionsKeys } from '@/components/views/Admin/private/ContentManagement/InstitutionalProgramsContent/PermissionsKey';
import { permissionsKeys as PqrsdContentManagementPermissionsKeys } from '@/components/views/Admin/private/ContentManagement/PqrsdAction/PermissionsKey';
import { permissionsKeys as PressRoomContentManagementPermissionsKeys } from '@/components/views/Admin/private/ContentManagement/PressRoomManagement/PermissionsKey';
import { permissionsKeys as ProceduresAndServicesContentManagementPermissionsKeys } from '@/components/views/Admin/private/ContentManagement/ProceduresAndServices/PermissionsKey';
import { permissionsKeys as RegulationsContentManagementPermissionsKeys } from '@/components/views/Admin/private/ContentManagement/Regulation/PermissionsKey';
import { permissionsKeys as CustomSitePermissionsKeys } from '@/components/views/Admin/private/CustomSites/PermissionsKey';
import { useGetDependencyById } from '@/components/views/Admin/private/Dependencies/hooks/useGetDependencyById';
import { permissionsKeys as DocumentsContentManagementPermissionsKeys } from '@/components/views/Admin/private/Documents/PermissionsKeys';
import { permissionsKeys as MicroSitePermissionsKeys } from '@/components/views/Admin/private/DynamicPage/PermissionsKey';
import { permissionsKeys as FooterObjectsPermissionsKeys } from '@/components/views/Admin/private/FooterObjects/PermissionsKey';
import { permissionsKeys as HeaderObjectsPermissionsKeys } from '@/components/views/Admin/private/HeaderObjects/PermissionsKey';
import { permissionsKeys as HomePagePermissionsKeys } from '@/components/views/Admin/private/HomePage/PermissionsKey';
import { permissionsKeys as ParametersPermissionsKeys } from '@/components/views/Admin/private/Parameters/PermissionsKey';
import { permissionsKeys as VersionPagePermissionsKeys } from '@/components/views/Admin/private/VersionPage/PermissionsKey';

import { useGetPageDynamics } from './hooks/useGetPageDynamics';
import { getCurrentIndexByRoute } from './utils';

/* -------------------------------------------------------------------------- */
/*                                   TYPES                                    */
/* -------------------------------------------------------------------------- */

interface SubmenuItem {
  id: number;
  title: string;
  link: string;
  /** prefijo de permiso para este sub-ítem */
  permissionKey?: string;
}

interface MenuItem {
  id: number;
  icon: JSX.Element;
  title: string;
  url?: string;
  permissionKey?: string; // 👈 prefijo de permiso del módulo
  submenu?: SubmenuItem[];
}

/* -------------------------------------------------------------------------- */
/*                          UTILIDADES PARA PERMISOS                          */
/* -------------------------------------------------------------------------- */

function hasPermission(userPerms: string[], key?: string): boolean {
  if (!key) return true; // sin clave ⇒ se muestra
  return userPerms.includes(key);
}

function filterMenu(items: MenuItem[], userPerms: string[], userType?: string): MenuItem[] {
  // Si es SuperAdmin, mostrar todos los elementos del menú sin filtrar
  if (userType === 'SuperAdmin') {
    return items.map((item) => ({
      ...item,
      submenu: item.submenu || [], // Mantener todos los submenús
    }));
  }

  // Para otros tipos de usuario, filtrar por permisos
  return items
    .map((item) => {
      if (!hasPermission(userPerms, item.permissionKey)) return null;

      const filteredSub =
        item.submenu?.filter((s) => hasPermission(userPerms, s.permissionKey)) ?? [];

      return { ...item, submenu: filteredSub };
    })
    .filter(Boolean) as MenuItem[];
}

/* -------------------------------------------------------------------------- */
/*                              CONSTANTES DE PERMISOS                         */
/* -------------------------------------------------------------------------- */

// Permisos para módulos que no tienen archivo específico de permisos
export const MicroSitesPermissionsKeys = {
  staticSites: 'micro-sites.static-sites',
  contentManagement: 'content-management',
  param: 'param',
} as const;

/* -------------------------------------------------------------------------- */
/*                              MENÚ SIN FILTRAR                              */
/* -------------------------------------------------------------------------- */

const rawMenuItems: MenuItem[] = [
  /* -------------------------------------------------------------------- */
  /* Administración de versiones                                          */
  /* -------------------------------------------------------------------- */
  {
    id: 0,
    permissionKey: VersionPagePermissionsKeys.adminVersion, // 🔑
    icon: <IconPages strokeWidth={2.5} className={cx('h-4 w-4')} />,
    title: 'Administración de versiones',
    url: ROUTE_ADMIN_VERSION_PAGE_PAGE_VERSION,
    submenu: [
      {
        id: 1,
        permissionKey: VersionPagePermissionsKeys.adminVersionFind, // 🔑
        title: 'Versión de página',
        link: ROUTE_ADMIN_VERSION_PAGE_PAGE_VERSION,
      },
    ],
  },

  /* -------------------------------------------------------------------- */
  /* Configuración de la página                                           */
  /* -------------------------------------------------------------------- */
  {
    id: 1,
    permissionKey: 'page-config', // 🔑
    icon: <IconConfigPage strokeWidth={2.5} className={cx('h-5 w-5')} />,
    title: 'Configuración de la página principal',
    url: ROUTE_ADMIN_PAGE_CONFIG,
    submenu: [
      {
        id: 2,
        permissionKey: HeaderObjectsPermissionsKeys.pageBlockFind, // 🔑
        title: 'Objetos de cabecera',
        link: ROUTE_ADMIN_HEADER_OBJECTS,
      },
      {
        id: 3,
        permissionKey: FooterObjectsPermissionsKeys.pageBlockFind, // 🔑
        title: 'Objetos de piecera',
        link: ROUTE_ADMIN_FOOTER_OBJECTS,
      },
      {
        id: 4,
        permissionKey: HomePagePermissionsKeys.pageBlockFind, // 🔑
        title: 'Página de inicio',
        link: ROUTE_ADMIN_HOME_PAGE,
      },
    ],
  },

  /* -------------------------------------------------------------------- */
  /* Micrositios                                             */
  /* -------------------------------------------------------------------- */
  {
    id: 7,
    permissionKey: 'micro-sites', // 🔑
    icon: <SidePen strokeWidth={2.5} className={cx('h-5 w-5')} />,
    title: 'Micro-sitios',
    url: ROUTE_ADMIN_MICRO_SITES,
    submenu: [
      {
        id: 1,
        permissionKey: MicroSitesPermissionsKeys.staticSites, // 🔑
        title: 'Sitios predeterminados',
        link: ROUTE_ADMIN_MICRO_SITES_DEFAULT_PAGE,
      },
      {
        id: 2,
        permissionKey: CustomSitePermissionsKeys.pageBlockFind, // 🔑
        title: 'Sitios personalizados',
        link: ROUTE_ADMIN_MICRO_SITES_CUSTOM_PAGE,
      },
      {
        id: 5,
        permissionKey: MicroSitePermissionsKeys.pageBlockFind, // 🔑
        title: 'Páginas de navegación',
        link: ROUTE_ADMIN_MICRO_SITES_DYNAMIC_PAGE,
      },
      {
        id: 6,
        permissionKey: TemplatesPermissionsKeys.pageBlockFind, // 🔑
        title: 'Mis plantillas',
        link: ROUTE_ADMIN_TEMPLATES,
      },
    ],
  },

  /* -------------------------------------------------------------------- */
  /* Administrador de contenido                                           */
  /* -------------------------------------------------------------------- */
  {
    id: 2,
    permissionKey: MicroSitesPermissionsKeys.contentManagement, // 🔑
    icon: <SideContent strokeWidth={2.5} className={cx('h-5 w-5')} />,
    title: 'Administrador de contenido',
    submenu: [
      {
        id: 2,
        title: 'Documentos',
        link: ROUTE_DOCUMENTS_MANAGEMENT,
        permissionKey: DocumentsContentManagementPermissionsKeys.pageBlockFind, // 🔑
      },

      {
        id: 1,
        permissionKey: ProceduresAndServicesContentManagementPermissionsKeys.pageBlockFind, // 🔑
        title: 'Trámites y servicios',
        link: ROUTE_PROCEDURES_SERVICES_MANAGEMENT,
      },
      {
        id: 3,
        permissionKey: RegulationsContentManagementPermissionsKeys.pageBlockFind, // 🔑
        title: 'Normatividad',
        link: ROUTE_REGULATION_MANAGEMENT,
      },
      {
        id: 4,
        permissionKey: PressRoomContentManagementPermissionsKeys.pageBlockFind, // 🔑
        title: 'Gestión de noticias',
        link: ROUTE_PRESS_ROOM_MANAGEMENT,
      },
      {
        id: 5,
        permissionKey: EventsPermissionsKeys.pageBlockFind, // 🔑
        title: 'Gestión de eventos',
        link: ROUTE_EVENT_MANAGEMENT,
      },
      {
        id: 6,
        permissionKey: InstitutionalProgramsContentManagementPermissionsKeys.pageBlockFind, // 🔑
        title: 'Programas institucionales',
        link: ROUTE_INSTITUTIONAL_PROGRAMS_MANAGEMENT,
      },
      {
        id: 8,
        permissionKey: PqrsdContentManagementPermissionsKeys.pageBlockFind, // 🔑
        title: 'PQRSDF',
        link: ROUTE_PQRSD_CONTENT_MANAGEMENT,
      },
    ],
  },

  /* -------------------------------------------------------------------- */
  /* Parametrización                                                      */
  /* -------------------------------------------------------------------- */
  {
    id: 6,
    permissionKey: MicroSitesPermissionsKeys.param, // 🔑
    icon: <SvgConfiguration strokeWidth={2.5} className={cx('h-5 w-5')} />,
    title: 'Parametrización',
    submenu: [
      {
        id: 0,
        permissionKey: MicroSitesPermissionsKeys.param, // 🔑
        title: 'Parámetros del sistema',
        link: ROUTE_ADMIN_PARAMETRIZATION,
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*                                 COMPONENTE                                 */
/* -------------------------------------------------------------------------- */

export function SideMenuAdmin() {
  const { route, push } = useRouter();
  const pathname = usePathname();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [open, setOpen] = useState<number | undefined>(getCurrentIndexByRoute(route));
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isVersionHovered, setIsVersionHovered] = useState(false);
  const { id, description: pageDescription, state, consecutive } = useSelectedPageStore();
  const userInfo = useUserStore((state) => state.user);

  const dependencyId = useUserStore((state) => state.dependencyId);

  const { data: dependencyData } = useGetDependencyById({
    dependencyId: dependencyId || '',
    options: { enabled: userInfo?.type === UserTypes.SuperAdmin && Boolean(dependencyId) },
  });

  // const { data, isLoading } = useGetPageDynamics({
  //   options: {
  //     pagination: { skip: 0, take: 9999 },
  //     where: { dependency: { _eq: dependencyId || '' } },
  //   },
  //   queryOptions: { enabled: Boolean(dependencyId) },
  // });

  /* ------------------------- cargar permisos y filtrar ------------------------- */
  useEffect(() => {
    const raw = localStorage.getItem('userauth');
    if (!raw) return;

    const userAuth = JSON.parse(raw);
    const perms: string[] = userAuth?.permissions ?? [];
    const userType = userAuth?.type;

    let allowed = filterMenu(rawMenuItems, perms, userType);

    setMenuItems(allowed);
  }, [localStorage.getItem('userauth'), userInfo]);

  useEffect(() => {
    // if (dependencyId && data) {
    if (dependencyId) {
      // const currentDependency =
      //   userInfo?.userDependencies?.find(
      //     (userDependency) => userDependency.dependency.id === dependencyId
      //   )?.dependency || dependencyData?.dependency;
      // const isMainDependency = currentDependency?.type === DependencyType.Main;

      const itemsToSend = rawMenuItems;

      // const itemsToSend = produce(rawMenuItems, (draft) => {
      //   const navigationPageIndex = draft.findIndex((item) => item.url === 'navigationpage');

      //   if (navigationPageIndex >= 0) {
      //     const subItemsToSend: SubmenuItem[] = data?.pageDynamics?.map((pageDynamics, index) => ({
      //       id: Math.random() * 1000 + index,
      //       title: pageDynamics.name,
      //       link: `${ROUTE_ADMIN_MICRO_SITES_PAGE}/${pageDynamics.id || ''}`,
      //       // Las páginas dinámicas no tienen permissionKey específico por ahora
      //       // Se podría agregar en el futuro si es necesario
      //     }));
      //     if (isMainDependency) {
      //       draft[navigationPageIndex].submenu = [
      //         ...(draft[navigationPageIndex].submenu || []),
      //         ...subItemsToSend,
      //       ];
      //     } else {
      //       draft[navigationPageIndex].submenu = subItemsToSend;
      //     }
      //   }
      // });

      // 🔐 CORREGIR BUG: Aplicar filtro de permisos después de agregar páginas dinámicas
      const raw = localStorage.getItem('userauth');
      if (raw) {
        const userAuth = JSON.parse(raw);
        const perms: string[] = userAuth?.permissions ?? [];
        const userType = userAuth?.type;
        const filteredItems = filterMenu(itemsToSend, perms, userType);
        setMenuItems(filteredItems);
      } else {
        // Si no hay permisos, no mostrar nada
        setMenuItems([]);
      }
    }
  }, [userInfo, dependencyData, rawMenuItems]);
  // }, [userInfo, data, dependencyData, rawMenuItems]);

  /* ------------------------------- listeners UI ------------------------------- */
  useEffect(() => {
    setSelectedItem(pathname);
  }, [pathname]);

  const handleOpen = (value: number) => setOpen(open === value ? -1 : value);
  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const isParentActive = (item: MenuItem) => {
    return item.submenu?.some((s) => isCurrentPathOrChild(selectedItem as string, s.link));
  };

  const handleMouseEnter = (id: number) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredItem(id);
  };
  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredItem(null), 300);
  };

  const TogleMenuItem = isCollapsed ? PanelLeftOpen : PanelLeftClose;

  /* -------------------------------- RENDER UI -------------------------------- */
  return (
    <Card
      className={cx(
        'max-w-[20rem] rounded-md border-r-2 border-gray-300 px-2 shadow-xl shadow-blue-gray-900/5 transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-16' : 'w-full'
      )}
    >
      {/* encabezado */}
      <div className={cx('mt-2 flex flex-col ', isCollapsed ? 'p-2' : 'space-y-4 p-2')}>
        <div
          className={cx('flex animate-fade-in flex-row items-center justify-between', {
            'gap-2': !isCollapsed,
          })}
        >
          <HoverCard>
            <HoverCardTrigger>
              <div
                className={cx(
                  'flex-1 cursor-default transition-all duration-300',
                  isCollapsed ? 'm-0 w-0 p-0 opacity-0' : 'w-auto opacity-100'
                )}
              >
                <p className="truncate  text-base font-semibold text-tundora">
                  Versión: {consecutive?.substring(0, 10)}
                </p>
                <p className="truncate font-atlassianSans text-sm">
                  {pageDescription?.substring(0, 15)}
                </p>
              </div>
            </HoverCardTrigger>
            <HoverCardContent
              sideOffset={5}
              alignOffset={5}
              align="start"
              className="w-full max-w-96"
            >
              <p className="line-clamp-1 text-nowrap font-montserrat text-base font-semibold text-tundora">
                Versión: {consecutive}
              </p>
              <p className="font-atlassianSans text-sm">{pageDescription}</p>
            </HoverCardContent>
          </HoverCard>

          <VersionLink
            id={id}
            state={state}
            consecutive={consecutive}
            pageDescription={pageDescription}
            isCollapsed={isCollapsed}
            isVersionHovered={isVersionHovered}
            setIsVersionHovered={setIsVersionHovered}
            visible={!isCollapsed}
          />
          <TogleMenuItem
            className="h-6 w-6 cursor-pointer self-center text-primary transition-all duration-300 hover:drop-shadow-md"
            onClick={toggleCollapse}
          />
        </div>
        {/* link versión para menú colapsado */}
        <VersionLink
          id={id}
          state={state}
          consecutive={consecutive}
          pageDescription={pageDescription}
          isCollapsed={isCollapsed}
          isVersionHovered={isVersionHovered}
          setIsVersionHovered={setIsVersionHovered}
          visible={isCollapsed}
        />
      </div>

      <hr className="mb-2 w-full border-[1px] border-primary" />

      {/* ---------- lista principal filtrada ---------- */}
      <List className={cx('min-w-0 p-0', isCollapsed ? 'w-fit' : 'w-full')}>
        {menuItems.map((item) => (
          <div
            key={item.id}
            className="relative"
            onMouseEnter={() => handleMouseEnter(item.id)}
            onMouseLeave={handleMouseLeave}
          >
            <Accordion
              // disabled={item.title === 'Micro-sitios'}
              open={open === item.id}
              icon={
                !isCollapsed &&
                item.submenu &&
                item.submenu.length > 0 && (
                  <SideArrowUp
                    strokeWidth={2.5}
                    className={`mx-auto h-4 w-4 transition-transform ${
                      open === item.id ? 'rotate-180' : ''
                    }`}
                  />
                )
              }
              className={cx('min-w-0 bg-transparent', {
                'bg-gray-50': isParentActive(item) && !isCollapsed,
                'w-12': isCollapsed,
              })}
            >
              <ListItem className="w-full p-0">
                <AccordionHeader
                  onClick={() => !isCollapsed && handleOpen(item.id)}
                  className={cx('border-b-0 p-3', { 'text-primaryDark': isParentActive(item) })}
                >
                  <ListItemPrefix className={cx('', { 'm-0': isCollapsed })}>
                    {React.cloneElement(item.icon, {
                      className: cx(item.icon.props.className, {
                        'text-primaryDark': isParentActive(item),
                      }),
                    })}
                  </ListItemPrefix>

                  {!isCollapsed && (
                    <Typography
                      color="blue-gray"
                      className={cx('mr-auto animate-fade-in font-montserrat font-semibold', {
                        'text-primaryDark': isParentActive(item),
                      })}
                    >
                      {item.title}
                    </Typography>
                  )}
                </AccordionHeader>
              </ListItem>

              {/* ---------- submenú ---------- */}
              {!isCollapsed && item.submenu && item.submenu.length > 0 && (
                <AccordionBody className="py-1 pl-8">
                  <List className="p-0 font-montserrat font-normal">
                    {item.submenu.map((sub) => (
                      <SubMenuItem
                        key={sub.id}
                        subItem={sub}
                        selectedItem={selectedItem}
                        handleRedirectLink={push}
                        setSelectedItem={setSelectedItem}
                      />
                    ))}
                  </List>
                </AccordionBody>
              )}
            </Accordion>

            {/* tooltip flotante cuando está colapsado */}
            {isCollapsed && hoveredItem === item.id && item.submenu && item.submenu.length > 0 && (
              <div
                className="absolute left-full top-0 z-50 ml-2 w-auto rounded-md bg-white shadow-lg"
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
              >
                <List className="p-0 font-montserrat font-normal">
                  {item.submenu.map((sub) => (
                    <SubMenuItem
                      key={sub.id}
                      subItem={sub}
                      selectedItem={selectedItem}
                      handleRedirectLink={push}
                      setSelectedItem={setSelectedItem}
                    />
                  ))}
                </List>
              </div>
            )}
          </div>
        ))}
      </List>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*                              SUB-COMPONENTES                               */
/* -------------------------------------------------------------------------- */

interface SubMenuItemProps {
  subItem: SubmenuItem;
  selectedItem: string | null;
  handleRedirectLink: (link: string) => void;
  setSelectedItem: (item: string) => void;
}

const SubMenuItem: React.FC<SubMenuItemProps> = ({
  subItem,
  selectedItem,
  handleRedirectLink,
  setSelectedItem,
}) => {
  const isSelected = isCurrentPathOrChild(selectedItem as string, subItem.link);

  return (
    <ListItem
      onClick={() => {
        setSelectedItem(subItem.link);
        handleRedirectLink(subItem.link);
      }}
      className={cx(
        'h-14 cursor-pointer',
        isSelected && 'border-l-4 border-l-[#FFAB00] font-semibold text-primary'
      )}
    >
      <span>{subItem.title}</span>
    </ListItem>
  );
};

interface VersionLinkProps {
  isCollapsed: boolean;
  state: VersionStates | undefined;
  id: string | undefined;
  consecutive: string | null | undefined;
  pageDescription: string | undefined;
  isVersionHovered: boolean;
  setIsVersionHovered: (v: boolean) => void;
  visible: boolean;
}

const VersionLink: React.FC<VersionLinkProps> = ({
  isCollapsed,
  state,
  id,
  consecutive,
  pageDescription,
  isVersionHovered,
  setIsVersionHovered,
  visible,
}) => {
  if (!visible) return null;

  return (
    <div className={cx('relative flex justify-center transition-all duration-300')}>
      <Link
        className={cx(
          'inline-block h-8 w-fit self-center overflow-hidden rounded-full border-2 border-transparent text-center font-bold capitalize transition-all duration-300',
          {
            'bg-greenLight text-greenDark': state === VersionStates.Active,
            'bg-concrete font-normal text-gray-700': state === VersionStates.Draft,
            'px-2 py-0': !isCollapsed,
            'flex h-8 w-8 items-center justify-center hover:border-blue-700 hover:shadow-sm hover:shadow-blue-600':
              isCollapsed,
          }
        )}
        href={`/?preview=${id}`}
        target="_blank"
        onMouseEnter={() => setIsVersionHovered(true)}
        onMouseLeave={() => setIsVersionHovered(false)}
      >
        {isCollapsed ? versionPageLabels[state!]!.charAt(0) : versionPageLabels[state!]}
      </Link>

      {isCollapsed && isVersionHovered && (
        <div className="absolute left-full top-0 z-50 ml-2 w-auto rounded-md bg-white p-2 shadow-lg transition-opacity duration-300">
          <p className="font-montserrat text-sm font-semibold">Versión: {consecutive}</p>
          <p className="font-atlassianSans text-xs">{pageDescription}</p>
          <p className="mt-1 font-montserrat text-sm font-semibold">Estado: {state}</p>
        </div>
      )}
    </div>
  );
};

export default SideMenuAdmin;
